import { useRollbar } from '@rollbar/react'
import * as pageActions from 'client/actions/pageActions'
import { useAppDispatch } from 'client/hooks/use-dispatch'
import usePageRouteDetector from 'client/hooks/usePageRouteDetector'
import { usePage, useWebsiteSettings } from 'client/store'
import pageSelectors, {
  isWebsitePageType,
} from 'client/store/page/pageSelectors'
import { BadRequest, PageValidationError } from 'common/errors'
import { AccessDenied } from 'common/errors/AccessDenied'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { useActions } from 'common/hooks/useActions'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PlayIcon } from 'src/editor/shared/icons/play-icon'
import { GhostIconButton } from 'src/editor/shared/ui-lib/buttons'
import { setIsPreviewMode } from 'src/editor/store/website-pages/website-pages-actions'
import {
  getWebsiteActivate,
  getWebsiteDomain,
} from 'src/editor/store/website-settings/website-settings-selector'

export const PreviewButton = () => {
  const { t, i18n } = useTranslation()
  const rollbar = useRollbar()
  const isLocaleInitialised = i18n.language
  const [isLoading, setIsLoading] = useState(false)
  const dispatch = useAppDispatch()
  const { isTemplate } = usePageRouteDetector()
  const previewPage = useActions(pageActions.previewPage)
  const pageId = usePage(pageSelectors.getPageId)

  const isWebsitePage = usePage(isWebsitePageType)
  const websiteDomain = useWebsiteSettings(getWebsiteDomain)
  const isWebsiteActivated = useWebsiteSettings(getWebsiteActivate)
  const isWebsitePreviewDisabled = isWebsitePage
    ? !isWebsiteActivated || !websiteDomain
    : false

  const handlePreview = async () => {
    setIsLoading(true)
    try {
      if (isWebsitePage) {
        dispatch(setIsPreviewMode(true))
        setIsLoading(false)
      } else {
        const previewWindow = window.open('', 'preview')
        const { data } = await previewPage(isTemplate)

        if (!previewWindow) {
          return
        }

        setIsLoading(false)
        previewWindow.location.href = data
      }
    } catch (error) {
      if (error instanceof PageValidationError) {
        dispatch(pageActions.pageValidationFail(error.messages))
      } else if (error instanceof NotFound) {
        rollbar.error(error)
      } else if (error instanceof AccessDenied) {
        dispatch(pageActions.previewPageFail('core.errors.login_required'))
      } else if (error instanceof NetworkError) {
        dispatch(pageActions.previewPageFail('core.errors.no_connection'))
      } else if (error instanceof BadRequest) {
        dispatch(pageActions.pageValidationFail(error.response.data))
      } else {
        dispatch(pageActions.previewPageFail('core.errors.preview'))
      }
    }

    setIsLoading(false)
  }

  return (
    <GhostIconButton
      onClick={handlePreview}
      title={t('core.header.preview')}
      disabled={
        !isLocaleInitialised || isLoading || !pageId || isWebsitePreviewDisabled
      }
      isLoading={isLoading}
      icon={<PlayIcon />}
    />
  )
}
